// CommercialWorkGallery.js - Commercial Work Portfolio Gallery component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a commercial work portfolio gallery with image grid and corporate styling
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the gallery is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The gallery elements
 */
export const createCommercialWorkGalleryElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const gallerySectionClass = 'commercial-gallery-section';
  const galleryContainerClass = 'commercial-gallery-container';
  const galleryHeaderClass = 'commercial-gallery-header';
  const galleryTitleClass = 'commercial-gallery-title';
  const gallerySubtitleClass = 'commercial-gallery-subtitle';
  const galleryGridClass = 'commercial-gallery-grid';
  const galleryImageItemClass = 'commercial-gallery-image-item';
  const galleryImageClass = 'commercial-gallery-image';
  const galleryImageOverlayClass = 'commercial-gallery-image-overlay';
  const galleryImageInfoClass = 'commercial-gallery-image-info';
  const galleryImageTitleClass = 'commercial-gallery-image-title';
  const galleryImageIndustryClass = 'commercial-gallery-image-industry';
  const galleryPatternOverlayClass = 'commercial-gallery-pattern-overlay';

  // Desktop Styles - Commercial theme with corporate design
  const gallerySectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f7f8fc',
    background: `
      radial-gradient(circle at 30% 20%, rgba(79, 70, 229, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(16, 185, 129, 0.04) 0%, transparent 50%),
      linear-gradient(135deg, #f7f8fc 0%, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%, #f7f8fc 100%)
    `,
    color: '#1e293b',
    width: '100%',
    overflow: 'hidden'
  };

  const galleryPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 25% 25%, rgba(79, 70, 229, 0.02) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.02) 1px, transparent 1px)
    `,
    backgroundSize: '90px 90px, 130px 130px',
    backgroundPosition: '0 0, 65px 65px',
    zIndex: '0',
    opacity: '0.3'
  };

  const galleryContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const galleryHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const galleryTitleStyles = {
    fontSize: '56px',
    fontWeight: '300',
    color: '#1e293b',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    lineHeight: '1.1',
    position: 'relative'
  };

  const gallerySubtitleStyles = {
    fontSize: '20px',
    color: 'rgba(30, 41, 59, 0.8)',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const galleryGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    marginTop: '40px'
  };

  const galleryImageItemStyles = {
    position: 'relative',
    height: '380px',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'rgba(255, 255, 255, 0.9)',
    border: '1px solid rgba(79, 70, 229, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
  };

  const galleryImageItemHoverStyles = {
    transform: 'translateY(-8px) scale(1.02)',
    border: '1px solid rgba(79, 70, 229, 0.25)',
    boxShadow: `
      0 20px 40px rgba(0, 0, 0, 0.1),
      0 0 25px rgba(79, 70, 229, 0.15)
    `
  };

  const galleryImageStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.5s ease',
    filter: 'brightness(1) contrast(1.05)'
  };

  const galleryImageHoverStyles = {
    filter: 'brightness(0.8) contrast(1.1)',
    transform: 'scale(1.05)'
  };

  const galleryImageOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(135deg, 
        rgba(79, 70, 229, 0.1) 0%, 
        rgba(16, 185, 129, 0.1) 40%, 
        rgba(30, 41, 59, 0.7) 100%
      )
    `,
    opacity: '0',
    transition: 'all 0.5s ease',
    zIndex: '1'
  };

  const galleryImageOverlayHoverStyles = {
    opacity: '1'
  };

  const galleryImageInfoStyles = {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    padding: '30px 25px',
    zIndex: '2',
    transform: 'translateY(20px)',
    opacity: '0',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const galleryImageInfoHoverStyles = {
    transform: 'translateY(0)',
    opacity: '1'
  };

  const galleryImageTitleStyles = {
    fontSize: '22px',
    fontWeight: '400',
    color: '#ffffff',
    marginBottom: '8px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '1px',
    lineHeight: '1.2'
  };

  const galleryImageIndustryStyles = {
    fontSize: '12px',
    color: '#10b981',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  // Tablet Styles
  const galleryTabletStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleTabletStyles = {
    fontSize: '48px'
  };

  const gallerySubtitleTabletStyles = {
    fontSize: '18px'
  };

  const galleryGridTabletStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px'
  };

  const galleryImageItemTabletStyles = {
    height: '340px'
  };

  // Mobile Styles
  const galleryMobileStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleMobileStyles = {
    fontSize: '40px'
  };

  const gallerySubtitleMobileStyles = {
    fontSize: '16px'
  };

  const galleryGridMobileStyles = {
    gridTemplateColumns: '1fr',
    gap: '20px'
  };

  const galleryImageItemMobileStyles = {
    height: '280px'
  };

  const galleryImageInfoMobileStyles = {
    padding: '25px 20px'
  };

  const galleryImageTitleMobileStyles = {
    fontSize: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(gallerySectionClass, gallerySectionStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryPatternOverlayClass, galleryPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryContainerClass, galleryContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryHeaderClass, galleryHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTitleClass, galleryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(gallerySubtitleClass, gallerySubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryGridClass, galleryGridStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageItemClass, galleryImageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageClass, galleryImageStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageOverlayClass, galleryImageOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageInfoClass, galleryImageInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageTitleClass, galleryImageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageIndustryClass, galleryImageIndustryStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageItemClass}--hover`, galleryImageItemHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageClass}--hover`, galleryImageHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageOverlayClass}--hover`, galleryImageOverlayHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageInfoClass}--hover`, galleryImageInfoHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(gallerySectionClass, galleryTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryTitleClass, galleryTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(gallerySubtitleClass, gallerySubtitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryGridClass, galleryGridTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryImageItemClass, galleryImageItemTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(gallerySectionClass, galleryMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryTitleClass, galleryTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(gallerySubtitleClass, gallerySubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryGridClass, galleryGridMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageItemClass, galleryImageItemMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageInfoClass, galleryImageInfoMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageTitleClass, galleryImageTitleMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    gallerySectionClass,
    galleryPatternOverlayClass,
    galleryContainerClass,
    galleryHeaderClass,
    galleryTitleClass,
    gallerySubtitleClass,
    galleryGridClass,
    galleryImageItemClass,
    galleryImageClass,
    galleryImageOverlayClass,
    galleryImageInfoClass,
    galleryImageTitleClass,
    galleryImageIndustryClass
  ]));

  // Commercial photography images data
  const commercialImages = [
    {
      src: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop&crop=center',
      title: 'Modern Office Space',
      industry: 'Architecture'
    },
    {
      src: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=400&fit=crop&crop=center',
      title: 'Restaurant Interior',
      industry: 'Hospitality'
    },
    {
      src: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop&crop=center',
      title: 'Retail Store Design',
      industry: 'Retail'
    },
    {
      src: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop&crop=center',
      title: 'Contemporary Interior',
      industry: 'Design'
    },
    {
      src: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&h=400&fit=crop&crop=center',
      title: 'Luxury Hotel Lobby',
      industry: 'Hospitality'
    },
    {
      src: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=600&h=400&fit=crop&crop=center',
      title: 'Fashion Boutique',
      industry: 'Fashion'
    },
    {
      src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop&crop=center',
      title: 'Corporate Headquarters',
      industry: 'Corporate'
    },
    {
      src: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=600&h=400&fit=crop&crop=center',
      title: 'Tech Startup Office',
      industry: 'Technology'
    },
    {
      src: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop&crop=center',
      title: 'Medical Facility',
      industry: 'Healthcare'
    },
    {
      src: 'https://images.unsplash.com/photo-1554469384-e58fac16e23a?w=600&h=400&fit=crop&crop=center',
      title: 'Manufacturing Plant',
      industry: 'Industrial'
    },
    {
      src: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop&crop=center',
      title: 'Urban Architecture',
      industry: 'Real Estate'
    },
    {
      src: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=600&h=400&fit=crop&crop=center',
      title: 'Creative Agency Space',
      industry: 'Creative'
    }
  ];

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const gridId = Date.now() + 6;

  // Create image elements
  let currentId = gridId + 1;
  const imageElements = [];
  const imageIds = [];

  commercialImages.forEach((image, index) => {
    const itemId = currentId++;
    const imageId = currentId++;
    const overlayId = currentId++;
    const infoId = currentId++;
    const titleId = currentId++;
    const industryId = currentId++;

    imageIds.push(itemId);

    imageElements.push(
      // Image Item Container
      {
        id: itemId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Commercial Image Item ${index + 1}`,
        parentId: gridId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageItemClass,
        styles: {},
        children: [imageId, overlayId, infoId],
        text: '',
        attributes: {
          'data-element': 'commercial-gallery-image-item',
          'data-index': index.toString()
        }
      },
      // Image
      {
        id: imageId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        name: `Commercial Image ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('image'),
        className: galleryImageClass,
        styles: {},
        src: image.src,
        attributes: {
          'data-element': 'commercial-gallery-image',
          'alt': image.title
        }
      },
      // Overlay
      {
        id: overlayId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Commercial Image Overlay ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageOverlayClass,
        styles: {},
        children: [],
        text: '',
        attributes: {
          'data-element': 'commercial-gallery-image-overlay'
        }
      },
      // Info
      {
        id: infoId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Commercial Image Info ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageInfoClass,
        styles: {},
        children: [titleId, industryId],
        text: '',
        attributes: {
          'data-element': 'commercial-gallery-image-info'
        }
      },
      // Title
      {
        id: titleId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h4',
        name: `Commercial Image Title ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('h4'),
        className: galleryImageTitleClass,
        styles: {},
        children: [],
        text: image.title,
        attributes: {
          'data-element': 'commercial-gallery-image-title'
        }
      },
      // Industry
      {
        id: industryId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Commercial Image Industry ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('span'),
        className: galleryImageIndustryClass,
        styles: {},
        children: [],
        text: image.industry,
        attributes: {
          'data-element': 'commercial-gallery-image-industry'
        }
      }
    );
  });

  // Create all elements
  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Commercial Work Gallery Section',
      parentId: null,
      className: gallerySectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'commercial-gallery-section',
        'data-component': 'section',
        'data-section-type': 'commercial-gallery',
        'data-page-id': currentPageId
      }
    },
    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Commercial Gallery Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'commercial-gallery-pattern-overlay'
      }
    },
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Commercial Gallery Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'commercial-gallery-container'
      }
    },
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Commercial Gallery Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'commercial-gallery-header'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Commercial Gallery Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: galleryTitleClass,
      styles: {},
      children: [],
      text: 'Commercial Work',
      attributes: {
        'data-element': 'commercial-gallery-title'
      }
    },
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Commercial Gallery Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: gallerySubtitleClass,
      styles: {},
      children: [],
      text: 'Professional commercial photography showcasing businesses, architecture, and corporate environments. Bringing brands to life through compelling visual storytelling.',
      attributes: {
        'data-element': 'commercial-gallery-subtitle'
      }
    },
    // Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Commercial Gallery Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryGridClass,
      styles: {},
      children: imageIds,
      text: '',
      attributes: {
        'data-element': 'commercial-gallery-grid'
      }
    },
    ...imageElements
  ];

  return {
    commercialGallerySection: newElements[0],
    elements: newElements
  };
}; 